import { ArrowUpRight } from "lucide-react";

const BRAND_CATEGORIES = [
  {
    id: "traditional",
    label: "01 — Traditional & Designer Wallpaper",
    intro:
      "Heritage houses and designer ateliers celebrated for craftsmanship, archival patterns, and timeless interiors.",
    brands: [
      {
        name: "Sanderson",
        description:
          "Synonymous with classic British elegance and timeless craftsmanship, offering a wide array of high-quality, nature-inspired, and botanical designs.",
        url: "https://www.sanderson.sandersondesigngroup.com",
      },
      {
        name: "York Wallcoverings",
        description:
          "One of the oldest and largest manufacturers in the U.S., known for high-quality, traditional patterns like damasks, florals, and textured prints.",
        url: "https://www.yorkwallcoverings.com",
      },
      {
        name: "Cole & Son",
        description:
          "A heritage brand celebrated for its artistic, iconic, and contemporary classic designs that often feature elaborate, high-end artistic prints.",
        url: "https://www.cole-and-son.com",
      },
      {
        name: "Boråstapeter",
        description:
          "A highly respected Swedish brand specializing in refined, minimalist, and beautifully crafted Scandinavian design.",
        url: "https://www.borastapeter.com",
      },
      {
        name: "Milton & King",
        description:
          "A premium, design-forward brand based in the USA, known for artistic, innovative patterns that blend modern aesthetics with quality craftsmanship.",
        url: "https://www.miltonandking.com",
      },
      {
        name: "Harlequin",
        description:
          "Famous for bold, high-energy, and contemporary wallcoverings that use vibrant color and unique textures to make a statement.",
        url: "https://harlequin.sandersondesigngroup.com",
      },
      {
        name: "Thibaut",
        description:
          "Known for a diverse and sophisticated range of patterns, including traditional, modern, and textured wallpapers that are highly favored by interior designers.",
        url: "https://www.thibautdesign.com",
      },
      {
        name: "GP & J Baker",
        description:
          "An iconic brand offering historical and elegant wallpaper designs, known for quality and prestige in the interior design world.",
        url: "https://gpjbaker.com",
      },
      {
        name: "Morris & Co.",
        description:
          "The quintessential name for Arts & Crafts movement designs, offering iconic, heritage-focused patterns with deep historical significance.",
        url: "https://morrisandco.sandersondesigngroup.com",
      },
      {
        name: "Little Greene",
        description:
          "Renowned for its sophisticated color palettes and high-quality, environmentally conscious wallcoverings.",
        url: "https://www.littlegreene.com",
      },
    ],
  },
  {
    id: "peel_and_stick",
    label: "02 — Peel & Stick (Removable)",
    intro:
      "Modern, renter-friendly innovators delivering designer-led removable wallpaper that installs in minutes.",
    brands: [
      {
        name: "Tempaper & Co.",
        description:
          "The innovators of removable peel-and-stick wallpaper, offering a wide range of designer-led styles that make temporary decor easy and aesthetic.",
        url: "https://tempaper.com",
      },
      {
        name: "NuWallpaper (WallPops)",
        description:
          "A leading brand for accessible, trendy, and easy-to-install peel-and-stick solutions, perfect for renters and frequent redecorators.",
        url: "https://www.brewsterwallcovering.com/peel-and-stick-wallpaper",
      },
      {
        name: "Livette's Wallpaper",
        description:
          "A top choice for professionals and homeowners, focusing on non-toxic, PVC-free materials and artist-collaborated, modern, and trendy designs.",
        url: "https://livetteswallpaper.com",
      },
      {
        name: "Chasing Paper",
        description:
          "Known for high-quality, user-friendly peel-and-stick options, often featuring unique designer collaborations and modern prints.",
        url: "https://chasingpaper.com",
      },
      {
        name: "RoomMates Decor",
        description:
          "A broad-reaching brand offering a massive variety of DIY-friendly wall decor and peel-and-stick options that are accessible to everyone.",
        url: "https://roommatesdecor.com",
      },
    ],
  },
];

export const BrandsPage = ({ themeConfig }) => {
  return (
    <div className="animate-fade-in" data-testid="brands-page">
      {/* Intro */}
      <section className="mb-16 md:mb-20 text-left">
        <span className="text-xs font-bold uppercase tracking-widest opacity-60 block mb-3">
          CURATED PARTNERS
        </span>
        <h1
          className={`text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight mb-4 ${themeConfig.fontFamilyHeading}`}
        >
          Our Brands
        </h1>
        <p className="text-base sm:text-lg max-w-2xl opacity-75 font-light leading-relaxed">
          A handpicked directory of the world&rsquo;s most respected wallpaper
          houses &mdash; from heritage British ateliers to modern peel-and-stick
          innovators &mdash; trusted across every R&amp;S Luxury Wall Design
          project.
        </p>
      </section>

      {BRAND_CATEGORIES.map((category) => (
        <section
          key={category.id}
          data-testid={`brand-category-${category.id}`}
          className="mb-20 border-t border-black/5 pt-12"
        >
          <div className="mb-10 max-w-2xl">
            <h2
              className={`text-2xl sm:text-3xl font-light tracking-tight mb-3 ${themeConfig.fontFamilyHeading}`}
            >
              {category.label}
            </h2>
            <p className="text-sm opacity-70 font-light leading-relaxed">
              {category.intro}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {category.brands.map((brand) => (
              <div
                key={brand.name}
                data-testid={`brand-card-${brand.name}`}
                className={`group flex flex-col justify-between p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${themeConfig.cardClass}`}
              >
                <div>
                  <h3
                    className={`text-xl font-medium tracking-tight mb-3 ${themeConfig.fontFamilyHeading}`}
                  >
                    {brand.name}
                  </h3>
                  <p className="text-sm opacity-70 leading-relaxed font-light mb-6">
                    {brand.description}
                  </p>
                </div>

                <a
                  href={brand.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`brand-cta-${brand.name}`}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 text-xs uppercase tracking-widest font-semibold transition-all ${themeConfig.accent}`}
                >
                  Visit Website
                  <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};
